import { useCallback, useState } from "react";
import { callAPI } from "../../../../utils/api";
import {
  discordNotificationTypeToNameMap,
  NotificationTypes,
} from "../../../../utils/constants/discordNotificationTypes";
import { useDiscordUser } from "../../../../utils/hooks/useDiscordUser";
import { useErrorToast, useSuccessToast } from "../../../../utils/showToast";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

const SUBSCRIBED_TITLE = msg({
  id: "Discord_notification_subscribed_title",
  message: "Subscribed",
});

const UNSUBSCRIBED_TITLE = msg({
  id: "Discord_notification_unsubscribed_title",
  message: "Unsubscribed",
});

const SUBSCRIBED_TOAST = msg({
  id: "Discord_notification_subscribed_toast",
  message: "You will now receive Discord notifications for {notificationName}.",
});

const UNSUBSCRIBED_TOAST = msg({
  id: "Discord_notification_unsubscribed_toast",
  message:
    "You will no longer receive Discord notifications for {notificationName}.",
});

const SUBSCRIBE_FAILED_TOAST = msg({
  id: "Discord_notification_subscribe_failed_toast",
  message: "Failed to subscribe: {errorMessage}",
});

const UNSUBSCRIBE_FAILED_TOAST = msg({
  id: "Discord_notification_unsubscribe_failed_toast",
  message: "Failed to unsubscribe: {errorMessage}",
});

export function useNotifications() {
  const { user, mutate } = useDiscordUser();
  const [isLoading, setIsLoading] = useState(false);
  const showSuccessToast = useSuccessToast();
  const showErrorToast = useErrorToast();
  const { _: i18n } = useLingui();

  const subscribe = useCallback(
    (notificationType: NotificationTypes) => {
      setIsLoading(true);
      callAPI(["discord", "notifications"], { method: "POST" })
        .then(() => {
          mutate().finally(() => {
            const name = i18n(
              discordNotificationTypeToNameMap[notificationType]
            );
            showSuccessToast(
              i18n(SUBSCRIBED_TOAST.id, { notificationName: name }),
              { title: i18n(SUBSCRIBED_TITLE) }
            );
          });
        })
        .catch((error) => {
          showErrorToast(
            i18n(SUBSCRIBE_FAILED_TOAST.id, {
              errorMessage: error.message,
            })
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [mutate, showSuccessToast, showErrorToast, i18n]
  );

  const unsubscribe = useCallback(
    (notificationType: NotificationTypes) => {
      setIsLoading(true);
      callAPI(["discord", "notifications"], { method: "DELETE" })
        .then(() => {
          mutate().finally(() => {
            const name = i18n(
              discordNotificationTypeToNameMap[notificationType]
            );
            showSuccessToast(
              i18n(UNSUBSCRIBED_TOAST.id, { notificationName: name }),
              { title: i18n(UNSUBSCRIBED_TITLE) }
            );
          });
        })
        .catch((error) => {
          showErrorToast(
            i18n(UNSUBSCRIBE_FAILED_TOAST.id, {
              errorMessage: error.message,
            })
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [mutate, showSuccessToast, showErrorToast, i18n]
  );

  const toggle = useCallback(
    (notificationType: NotificationTypes) => {
      if (!user) return;
      if (user.notifications.includes(notificationType) || false)
        unsubscribe(notificationType);
      else subscribe(notificationType);
    },
    [subscribe, unsubscribe, user]
  );

  return { isLoading, toggle };
}
